import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { toast } from "sonner";
import { Pencil, Trash2, X } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";
import BackupExcelButton from "@/components/BackupExcelButton";
import { DeskripsiButton } from "@/components/Deskripsi";
import { EKSKUL_LIST, PROGSUS_LIST, EP_ALL, EP_TAHUN, EP_SEMESTER, epSemesterLabel } from "@/lib/constants";

const F_EMPTY = { siswa: "", jenis: "", guru: "", tahun: "", semester: "" };

export default function EPNilaiKepsek() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ekskul-progsus/nilai");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = items.filter((it) => {
    if (applied.siswa && !(it.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.jenis && it.jenis !== applied.jenis) return false;
    if (applied.guru && !(it.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
    if (applied.tahun && String(it.tahun) !== String(applied.tahun)) return false;
    if (applied.semester && it.semester !== applied.semester) return false;
    return true;
  });

  const del = async (id) => {
    if (!window.confirm("Hapus nilai ini?")) return;
    try { await api.delete(`/ekskul-progsus/nilai/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/ekskul-progsus/nilai/${edit.id}`, {
        siswa_id: edit.siswa_id,
        jenis: edit.jenis,
        tahun: parseInt(edit.tahun, 10),
        semester: edit.semester,
        nilai: parseInt(edit.nilai, 10),
        deskripsi: edit.deskripsi || "",
      });
      toast.success("Nilai diperbarui");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Ekskul & Progsus — Nilai" action={<BackupExcelButton endpoint="ekskul-progsus-nilai" filters={{ jenis: applied.jenis, tahun: applied.tahun, semester: applied.semester }} testId="backup-ep-nilai-btn" />} />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="ep-nilai-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari nama siswa" },
          { key: "jenis", type: "select", label: "Jenis", options: EP_ALL },
          { key: "guru", type: "text", label: "Guru", placeholder: "Cari pendamping" },
          { key: "tahun", type: "select", label: "Tahun", options: EP_TAHUN.map(String) },
          { key: "semester", type: "select", label: "Semester", options: EP_SEMESTER.map((s) => ({ v: s.v, label: s.label })) },
        ]}
      />

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ep-nilai-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-4 py-3">Nama Siswa</th>
              <th className="text-left px-4 py-3">Jenis Ekskul & Progsus</th>
              <th className="text-left px-4 py-3">Pendamping</th>
              <th className="text-left px-4 py-3">Tahun</th>
              <th className="text-left px-4 py-3">Semester</th>
              <th className="text-left px-4 py-3">Nilai</th>
              <th className="text-left px-4 py-3">Deskripsi</th>
              <th className="text-right px-4 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {visible.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400 italic">Belum ada nilai.</td></tr>}
              {visible.map((it) => (
                <tr key={it.id} className="pp-row" data-testid={`ep-nilai-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium">{it.nama_siswa} <span className="text-xs text-slate-400 font-mono">({it.nis_siswa})</span></td>
                  <td className="px-4 py-3 text-xs">{it.jenis}</td>
                  <td className="px-4 py-3 text-xs">{it.nama_guru}</td>
                  <td className="px-4 py-3">{it.tahun}</td>
                  <td className="px-4 py-3 text-xs">{epSemesterLabel(it.semester)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{it.nilai}</td>
                  <td className="px-4 py-3"><DeskripsiButton text={it.deskripsi} dataTestid={`ep-nilai-desk-${it.id}`} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEdit({ ...it })} className="pp-btn-ghost text-xs" data-testid={`ep-nilai-edit-${it.id}`}>
                        <Pencil className="w-3.5 h-3.5" />Edit
                      </button>
                      <button onClick={() => del(it.id)} className="pp-btn-danger" data-testid={`ep-nilai-del-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold">Edit Nilai</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={submitEdit} className="grid gap-4">
              <div><label className="pp-label">Siswa</label><input readOnly className="pp-input bg-slate-50" value={edit.nama_siswa} /></div>
              <div><label className="pp-label">Jenis</label>
                <select className="pp-input" value={edit.jenis} onChange={(e) => setEdit({ ...edit, jenis: e.target.value })}>
                  <optgroup label="Ekstrakurikuler">{EKSKUL_LIST.map((k) => <option key={k} value={k}>{k}</option>)}</optgroup>
                  <optgroup label="Program Khusus">{PROGSUS_LIST.map((k) => <option key={k} value={k}>{k}</option>)}</optgroup>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="pp-label">Tahun</label>
                  <select className="pp-input" value={edit.tahun} onChange={(e) => setEdit({ ...edit, tahun: e.target.value })}>
                    {EP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div><label className="pp-label">Semester</label>
                  <select className="pp-input" value={edit.semester} onChange={(e) => setEdit({ ...edit, semester: e.target.value })}>
                    {EP_SEMESTER.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="pp-label">Nilai (0-100)</label>
                <input type="number" min={0} max={100} required className="pp-input" value={edit.nilai} onChange={(e) => setEdit({ ...edit, nilai: e.target.value })} data-testid="ep-nilai-edit-val" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEdit(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" className="pp-btn-primary" data-testid="ep-nilai-edit-save">Simpan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
